// SoundFx.js

const DEFAULT_EFFECTS = {
  enabled: true,

  lowCutEnabled: false,
  lowCutFrequency: 90,

  highCutEnabled: false,
  highCutFrequency: 14000,

  denoiseEnabled: false,
  denoiseAmount: 40,

  compressionEnabled: false,
  compressionAmount: 50,

  reverbEnabled: false,
  reverbMix: 0.2,
  reverbDuration: 1.8,
  reverbDecay: 2.5,

  delayEnabled: false,
  delayTime: 0.22,
  delayFeedback: 0.28,
  delayMix: 0.2,

  eqLow: 0,
  eqMid: 0,
  eqHigh: 0,

  outputGain: 1,
};

const clamp = (value, minimum, maximum) =>
  Math.min(maximum, Math.max(minimum, Number(value) || 0));

class SoundFx {
  constructor() {
    this.impulseCache = new Map();
  }

  getDefaultEffects(overrides = {}) {
    return {
      ...DEFAULT_EFFECTS,
      ...overrides,
    };
  }

  createReverbImpulse(audioContext, duration = 1.8, decay = 2.5) {
    const safeDuration = clamp(duration, 0.2, 8);
    const safeDecay = clamp(decay, 0.5, 8);

    const cacheKey = [audioContext.sampleRate, safeDuration, safeDecay].join(
      "-",
    );

    if (this.impulseCache.has(cacheKey)) {
      return this.impulseCache.get(cacheKey);
    }

    const length = Math.floor(audioContext.sampleRate * safeDuration);

    const impulse = audioContext.createBuffer(
      2,
      length,
      audioContext.sampleRate,
    );

    for (let channel = 0; channel < impulse.numberOfChannels; channel += 1) {
      const channelData = impulse.getChannelData(channel);

      for (let index = 0; index < length; index += 1) {
        const envelope = Math.pow(1 - index / length, safeDecay);

        channelData[index] = (Math.random() * 2 - 1) * envelope;
      }
    }

    this.impulseCache.set(cacheKey, impulse);

    return impulse;
  }

  connect({
    audioContext,
    inputNode,
    destinationNode,
    effects = {},
    volume = 1,
  }) {
    if (!audioContext || !inputNode || !destinationNode) {
      throw new Error(
        "SoundFx.connect requires audioContext, inputNode and destinationNode.",
      );
    }

    const settings = this.getDefaultEffects(effects);

    const inputGain = audioContext.createGain();
    const outputGain = audioContext.createGain();

    inputGain.gain.value = 1;

    outputGain.gain.value =
      settings.enabled === false
        ? clamp(volume, 0, 2)
        : clamp(volume * settings.outputGain, 0, 2);

    inputNode.connect(inputGain);

    if (settings.enabled === false) {
      inputGain.connect(outputGain);
      outputGain.connect(destinationNode);

      return {
        inputGain,
        outputGain,
        nodes: {},
        disconnect: () => {
          try {
            inputGain.disconnect();
            outputGain.disconnect();
          } catch {}
        },
      };
    }

    const nodes = {};

    let currentNode = inputGain;

    /*
      Denoise approximation.

      Web Audio cannot perform advanced AI noise removal by itself.
      This combination removes rumble and controls low-level noise.
    */
    if (settings.denoiseEnabled) {
      const denoiseHighPass = audioContext.createBiquadFilter();

      denoiseHighPass.type = "highpass";
      denoiseHighPass.frequency.value =
        55 + clamp(settings.denoiseAmount, 0, 100) * 1.5;
      denoiseHighPass.Q.value = 0.7;

      const denoiseCompressor = audioContext.createDynamicsCompressor();

      const denoiseAmount = clamp(settings.denoiseAmount, 0, 100);

      denoiseCompressor.threshold.value = -52 + denoiseAmount * 0.18;

      denoiseCompressor.knee.value = 6;
      denoiseCompressor.ratio.value = 2.2;
      denoiseCompressor.attack.value = 0.01;
      denoiseCompressor.release.value = 0.12;

      currentNode.connect(denoiseHighPass);
      denoiseHighPass.connect(denoiseCompressor);

      currentNode = denoiseCompressor;

      nodes.denoiseHighPass = denoiseHighPass;
      nodes.denoiseCompressor = denoiseCompressor;
    }

    if (settings.lowCutEnabled) {
      const lowCut = audioContext.createBiquadFilter();

      lowCut.type = "highpass";
      lowCut.frequency.value = clamp(settings.lowCutFrequency, 20, 1000);
      lowCut.Q.value = 0.7;

      currentNode.connect(lowCut);
      currentNode = lowCut;

      nodes.lowCut = lowCut;
    }

    if (settings.highCutEnabled) {
      const highCut = audioContext.createBiquadFilter();

      highCut.type = "lowpass";
      highCut.frequency.value = clamp(settings.highCutFrequency, 1000, 22000);
      highCut.Q.value = 0.7;

      currentNode.connect(highCut);
      currentNode = highCut;

      nodes.highCut = highCut;
    }

    const lowEq = audioContext.createBiquadFilter();
    lowEq.type = "lowshelf";
    lowEq.frequency.value = 180;
    lowEq.gain.value = clamp(settings.eqLow, -18, 18);

    const midEq = audioContext.createBiquadFilter();
    midEq.type = "peaking";
    midEq.frequency.value = 1500;
    midEq.Q.value = 0.9;
    midEq.gain.value = clamp(settings.eqMid, -18, 18);

    const highEq = audioContext.createBiquadFilter();
    highEq.type = "highshelf";
    highEq.frequency.value = 6000;
    highEq.gain.value = clamp(settings.eqHigh, -18, 18);

    currentNode.connect(lowEq);
    lowEq.connect(midEq);
    midEq.connect(highEq);

    currentNode = highEq;

    nodes.lowEq = lowEq;
    nodes.midEq = midEq;
    nodes.highEq = highEq;

    if (settings.compressionEnabled) {
      const compressor = audioContext.createDynamicsCompressor();

      const amount = clamp(settings.compressionAmount, 0, 100);

      compressor.threshold.value = -10 - amount * 0.28;
      compressor.knee.value = 18;
      compressor.ratio.value = 2 + amount * 0.08;
      compressor.attack.value = 0.008;
      compressor.release.value = 0.16;

      currentNode.connect(compressor);
      currentNode = compressor;

      nodes.compressor = compressor;
    }

    /*
      Dry signal.
    */
    const dryGain = audioContext.createGain();
    dryGain.gain.value = 1;

    currentNode.connect(dryGain);
    dryGain.connect(outputGain);

    nodes.dryGain = dryGain;

    /*
      Delay send.
    */
    if (settings.delayEnabled) {
      const delayNode = audioContext.createDelay(3);
      const delayFeedback = audioContext.createGain();
      const delayWet = audioContext.createGain();

      delayNode.delayTime.value = clamp(settings.delayTime, 0.01, 2);

      delayFeedback.gain.value = clamp(settings.delayFeedback, 0, 0.85);

      delayWet.gain.value = clamp(settings.delayMix, 0, 1);

      currentNode.connect(delayNode);
      delayNode.connect(delayFeedback);
      delayFeedback.connect(delayNode);

      delayNode.connect(delayWet);
      delayWet.connect(outputGain);

      nodes.delayNode = delayNode;
      nodes.delayFeedback = delayFeedback;
      nodes.delayWet = delayWet;
    }

    /*
      Reverb send.
    */
    if (settings.reverbEnabled) {
      const convolver = audioContext.createConvolver();
      const reverbWet = audioContext.createGain();

      convolver.buffer = this.createReverbImpulse(
        audioContext,
        settings.reverbDuration,
        settings.reverbDecay,
      );

      reverbWet.gain.value = clamp(settings.reverbMix, 0, 1);

      currentNode.connect(convolver);
      convolver.connect(reverbWet);
      reverbWet.connect(outputGain);

      nodes.convolver = convolver;
      nodes.reverbWet = reverbWet;
    }

    outputGain.connect(destinationNode);

    return {
      inputGain,
      outputGain,
      nodes,

      update: (nextEffects = {}, nextVolume = volume) => {
        const updatedSettings = this.getDefaultEffects({
          ...settings,
          ...nextEffects,
        });

        const now = audioContext.currentTime;

        outputGain.gain.setTargetAtTime(
          clamp(nextVolume * updatedSettings.outputGain, 0, 2),
          now,
          0.02,
        );

        if (nodes.lowCut) {
          nodes.lowCut.frequency.setTargetAtTime(
            clamp(updatedSettings.lowCutFrequency, 20, 1000),
            now,
            0.02,
          );
        }

        if (nodes.highCut) {
          nodes.highCut.frequency.setTargetAtTime(
            clamp(updatedSettings.highCutFrequency, 1000, 22000),
            now,
            0.02,
          );
        }

        nodes.lowEq?.gain.setTargetAtTime(
          clamp(updatedSettings.eqLow, -18, 18),
          now,
          0.02,
        );

        nodes.midEq?.gain.setTargetAtTime(
          clamp(updatedSettings.eqMid, -18, 18),
          now,
          0.02,
        );

        nodes.highEq?.gain.setTargetAtTime(
          clamp(updatedSettings.eqHigh, -18, 18),
          now,
          0.02,
        );

        nodes.delayNode?.delayTime.setTargetAtTime(
          clamp(updatedSettings.delayTime, 0.01, 2),
          now,
          0.02,
        );

        nodes.delayFeedback?.gain.setTargetAtTime(
          clamp(updatedSettings.delayFeedback, 0, 0.85),
          now,
          0.02,
        );

        nodes.delayWet?.gain.setTargetAtTime(
          clamp(updatedSettings.delayMix, 0, 1),
          now,
          0.02,
        );

        nodes.reverbWet?.gain.setTargetAtTime(
          clamp(updatedSettings.reverbMix, 0, 1),
          now,
          0.02,
        );
      },

      disconnect: () => {
        Object.values(nodes).forEach((node) => {
          try {
            node.disconnect();
          } catch {}
        });

        try {
          inputGain.disconnect();
          outputGain.disconnect();
        } catch {}
      },
    };
  }

  async render({ source, effects = {}, volume = 1, tailSeconds }) {
    if (!source) {
      throw new Error("No audio source supplied.");
    }

    let arrayBuffer;

    if (source instanceof Blob) {
      arrayBuffer = await source.arrayBuffer();
    } else if (source instanceof ArrayBuffer) {
      arrayBuffer = source;
    } else {
      const response = await fetch(source);

      if (!response.ok) {
        throw new Error("Unable to download audio for processing.");
      }

      arrayBuffer = await response.arrayBuffer();
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;

    const decodingContext = new AudioContextClass();

    const decodedBuffer = await decodingContext.decodeAudioData(
      arrayBuffer.slice(0),
    );

    await decodingContext.close();

    const settings = this.getDefaultEffects(effects);

    const calculatedTail =
      tailSeconds ?? (settings.reverbEnabled || settings.delayEnabled ? 3 : 0);

    const outputLength = Math.ceil(
      decodedBuffer.sampleRate * (decodedBuffer.duration + calculatedTail),
    );

    const offlineContext = new OfflineAudioContext(
      decodedBuffer.numberOfChannels,
      outputLength,
      decodedBuffer.sampleRate,
    );

    const sourceNode = offlineContext.createBufferSource();

    sourceNode.buffer = decodedBuffer;

    this.connect({
      audioContext: offlineContext,
      inputNode: sourceNode,
      destinationNode: offlineContext.destination,
      effects: settings,
      volume,
    });

    sourceNode.start(0);

    const renderedBuffer = await offlineContext.startRendering();

    return this.audioBufferToWavBlob(renderedBuffer);
  }

  audioBufferToWavBlob(audioBuffer) {
    const numberOfChannels = audioBuffer.numberOfChannels;

    const sampleRate = audioBuffer.sampleRate;
    const bytesPerSample = 2;
    const blockAlign = numberOfChannels * bytesPerSample;

    const dataLength = audioBuffer.length * blockAlign;

    const arrayBuffer = new ArrayBuffer(44 + dataLength);

    const view = new DataView(arrayBuffer);

    const writeString = (offset, value) => {
      for (let index = 0; index < value.length; index += 1) {
        view.setUint8(offset + index, value.charCodeAt(index));
      }
    };

    writeString(0, "RIFF");
    view.setUint32(4, 36 + dataLength, true);
    writeString(8, "WAVE");
    writeString(12, "fmt ");

    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true);

    writeString(36, "data");
    view.setUint32(40, dataLength, true);

    const channelData = [];

    for (let channel = 0; channel < numberOfChannels; channel += 1) {
      channelData.push(audioBuffer.getChannelData(channel));
    }

    let offset = 44;

    for (
      let sampleIndex = 0;
      sampleIndex < audioBuffer.length;
      sampleIndex += 1
    ) {
      for (let channel = 0; channel < numberOfChannels; channel += 1) {
        const sample = Math.max(
          -1,
          Math.min(1, channelData[channel][sampleIndex]),
        );

        view.setInt16(
          offset,
          sample < 0 ? sample * 0x8000 : sample * 0x7fff,
          true,
        );

        offset += bytesPerSample;
      }
    }

    return new Blob([arrayBuffer], {
      type: "audio/wav",
    });
  }
}
const soundFx = new SoundFx();

export { DEFAULT_EFFECTS };
export default soundFx;
